import { Outlet, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useCallback, useEffect, useState, useMemo } from "react";
import {
    LayoutDashboard, Inbox, ImageIcon, Wrench, MessageSquare, Settings, LogOut,
    Layers, FileText, Search, Sparkles, MapPin, BookOpen, Mail, BarChart3,
    ChevronDown, ChevronRight, Globe, UserCheck, Hammer, HardHat
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { PARTNERS_CHANGED } from "@/components/admin/partners/PartnerUpdatesAdmin";

export const CONVERSATIONS_CHANGED = "admin:conversations-changed";

const TOP_LINKS = [
    { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
    { to: "/admin/leads", label: "Pedidos", icon: Inbox, end: true },
    { to: "/admin/clientes", label: "Clientes", icon: UserCheck, end: true },
    { to: "/admin/obras-em-curso", label: "Obras em curso", icon: Hammer, end: true, highlight: true, testid: "admin-nav-obras-em-curso" },
    { to: "/admin/parceiros", label: "Parceiros", icon: HardHat, end: true, testid: "admin-nav-parceiros" },
];

const SITE_GROUP = {
    key: "site",
    label: "Site",
    icon: Globe,
    items: [
        { to: "/admin/projetos", label: "Portfólio", icon: ImageIcon, testid: "admin-nav-portfolio" },
        { to: "/admin/antes-depois", label: "Antes & Depois", icon: Sparkles },
        { to: "/admin/servicos", label: "Serviços", icon: Wrench },
        { to: "/admin/especialidades", label: "Especialidades", icon: Layers },
        { to: "/admin/testemunhos", label: "Testemunhos", icon: MessageSquare },
        { to: "/admin/conteudos", label: "Conteúdos", icon: FileText },
        { to: "/admin/landings", label: "Landing pages", icon: MapPin },
        { to: "/admin/seo", label: "SEO", icon: Search },
    ],
};

const AFTER_LINKS = [
    { to: "/admin/blog", label: "Blog", icon: BookOpen },
    { to: "/admin/newsletter", label: "Nota de Obra", icon: Mail },
    { to: "/admin/analytics", label: "Email marketing", icon: BarChart3 },
];

const SETTINGS_LINK = { to: "/admin/definicoes", label: "Definições", icon: Settings };

// Flat list for mobile select, preserving grouping via optgroup
const MOBILE_GROUPS = [
    { label: null, items: TOP_LINKS },
    { label: SITE_GROUP.label, items: SITE_GROUP.items },
    { label: null, items: AFTER_LINKS },
    { label: null, items: [SETTINGS_LINK] },
];

function NavItem({ to, end, label, Icon, testid, highlight, badge, badgeLabel }) {
    const id = testid || `admin-nav-${label.toLowerCase()}`;
    return (
        <NavLink
            to={to}
            end={end}
            data-testid={id}
            className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm transition ${
                    isActive
                        ? "bg-[hsl(var(--copper))] text-white font-semibold"
                        : highlight
                            ? "text-white font-semibold bg-white/10 ring-1 ring-inset ring-[hsl(var(--copper))] hover:bg-white/15"
                            : "text-white/75 hover:bg-white/5 hover:text-white"
                }`}
        >
            <Icon className="w-4 h-4" />
            <span className="flex-1">{label}</span>
            {badge > 0 && (
                <span
                    className="min-w-[20px] h-5 px-1.5 rounded-full bg-white text-[hsl(var(--forest))] text-[11px] font-bold flex items-center justify-center"
                    aria-label={badgeLabel || (badge === 1 ? "1 nova mensagem" : `${badge} novas mensagens`)}
                    title={badgeLabel || (badge === 1 ? "1 nova mensagem" : `${badge} novas mensagens`)}
                    data-testid={`${id}-badge`}
                >
                    {badge}
                </span>
            )}
        </NavLink>
    );
}

export default function AdminLayout() {
    const { logout, user } = useAuth();
    const nav = useNavigate();
    const { pathname } = useLocation();
    const doLogout = async () => { await logout(); nav("/admin/login"); };

    // Force browser tab title while inside the backoffice
    useEffect(() => {
        const prev = document.title;
        document.title = "Gestão | O Homem da Obra";
        return () => { document.title = prev; };
    }, []);

    const siteGroupHasActive = useMemo(
        () => SITE_GROUP.items.some((i) => pathname.startsWith(i.to)),
        [pathname]
    );

    // Contador de mensagens novas dos clientes (sem tempo real: atualiza ao navegar, ao voltar ao separador e após ações nas conversas).
    const [unread, setUnread] = useState(0);
    const [partnerTodo, setPartnerTodo] = useState(0);
    const loadUnread = useCallback(() => {
        api.get("/admin/conversations/unread-count").then(({ data }) => setUnread(data.count || 0)).catch(() => {});
        api.get("/admin/partner-summary").then(({ data }) => setPartnerTodo((data.pending_updates || 0) + (data.unread_threads || 0))).catch(() => {});
    }, []);
    useEffect(() => { loadUnread(); }, [pathname, loadUnread]);
    useEffect(() => {
        window.addEventListener("focus", loadUnread);
        window.addEventListener(CONVERSATIONS_CHANGED, loadUnread);
        window.addEventListener(PARTNERS_CHANGED, loadUnread);
        return () => {
            window.removeEventListener("focus", loadUnread);
            window.removeEventListener(CONVERSATIONS_CHANGED, loadUnread);
            window.removeEventListener(PARTNERS_CHANGED, loadUnread);
        };
    }, [loadUnread]);
    const badgeFor = (to) => (to === "/admin/clientes" ? unread : to === "/admin/parceiros" ? partnerTodo : 0);
    const badgeLabelFor = (to) => (to === "/admin/parceiros" ? `${partnerTodo} por tratar` : undefined);

    const [openSite, setOpenSite] = useState(siteGroupHasActive);
    useEffect(() => {
        if (siteGroupHasActive) setOpenSite(true);
    }, [siteGroupHasActive]);

    return (
        <div className="min-h-screen bg-[hsl(var(--cream))] flex">
            <aside className="w-64 bg-[hsl(var(--forest))] text-white flex-shrink-0 hidden md:flex flex-col">
                <div className="p-6 border-b border-white/10">
                    <div className="flex items-center gap-2 mb-1">
                        <span className="logo-mark text-3xl text-white">H<span className="dot">.</span></span>
                        <span className="font-display font-bold text-white text-sm">O Homem da Obra</span>
                    </div>
                    <div className="text-[10px] uppercase tracking-widest text-white/50 font-mono mt-1">Painel de gestão</div>
                    <div className="text-xs text-white/60 mt-3 truncate">{user?.email}</div>
                </div>
                <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
                    {TOP_LINKS.map((n) => (
                        <NavItem key={n.to} to={n.to} end={n.end} label={n.label} Icon={n.icon} testid={n.testid} highlight={n.highlight} badge={badgeFor(n.to)} badgeLabel={badgeLabelFor(n.to)} />
                    ))}

                    {/* Site group (collapsible) */}
                    <button
                        type="button"
                        onClick={() => setOpenSite((v) => !v)}
                        data-testid="admin-nav-group-site"
                        aria-expanded={openSite}
                        className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm transition ${
                            siteGroupHasActive
                                ? "text-white font-semibold bg-white/5"
                                : "text-white/75 hover:bg-white/5 hover:text-white"
                        }`}
                    >
                        <SITE_GROUP.icon className="w-4 h-4" />
                        <span className="flex-1 text-left">{SITE_GROUP.label}</span>
                        {openSite ? <ChevronDown className="w-4 h-4 opacity-70" /> : <ChevronRight className="w-4 h-4 opacity-70" />}
                    </button>
                    {openSite && (
                        <div className="ml-3 pl-3 border-l border-white/10 space-y-1" data-testid="admin-nav-group-site-items">
                            {SITE_GROUP.items.map((n) => (
                                <NavItem key={n.to} to={n.to} label={n.label} Icon={n.icon} testid={n.testid} />
                            ))}
                        </div>
                    )}

                    {AFTER_LINKS.map((n) => (
                        <NavItem key={n.to} to={n.to} label={n.label} Icon={n.icon} />
                    ))}

                    <div className="h-2" />
                    <div className="border-t border-white/10 pt-2">
                        <NavItem to={SETTINGS_LINK.to} label={SETTINGS_LINK.label} Icon={SETTINGS_LINK.icon} />
                    </div>
                </nav>
                <button onClick={doLogout} data-testid="admin-logout"
                    className="m-3 flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm text-white/70 hover:bg-white/5 hover:text-white transition">
                    <LogOut className="w-4 h-4" /> Sair
                </button>
            </aside>

            <div className="md:hidden fixed top-0 left-0 right-0 z-30 bg-[hsl(var(--forest))] text-white p-3 flex items-center justify-between">
                <span className="logo-mark text-xl text-white flex items-center gap-2">H<span className="dot">.</span></span>
                <select
                    onChange={(e) => nav(e.target.value)}
                    value={pathname}
                    className="bg-[hsl(var(--forest-deep))] border border-white/10 rounded px-2 py-1 text-sm text-white"
                    data-testid="admin-nav-mobile-select"
                >
                    {MOBILE_GROUPS.map((g, idx) => (
                        g.label ? (
                            <optgroup key={`grp-${idx}`} label={g.label}>
                                {g.items.map((n) => <option key={n.to} value={n.to}>{n.label}</option>)}
                            </optgroup>
                        ) : (
                            g.items.map((n) => (
                                <option key={n.to} value={n.to}>{badgeFor(n.to) > 0 ? `${n.label} (${badgeFor(n.to)})` : n.label}</option>
                            ))
                        )
                    ))}
                </select>
                <button onClick={doLogout} className="text-xs text-[hsl(var(--copper))]">Sair</button>
            </div>

            <main className="flex-1 overflow-y-auto p-6 md:p-10 pt-20 md:pt-10">
                <Outlet />
            </main>
        </div>
    );
}
